import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { gapsLabel } from '../api/compliance.ts';
import { crewQuery, rolesQuery } from '../api/crew.ts';
import { jobsQuery, updateJob, type Job } from '../api/jobs.ts';
import { localLabel } from '../api/quotes.ts';
import {
  assignTask,
  createTask,
  FIT,
  MismatchProblem,
  setTaskStatus,
  taskCandidatesQuery,
  unassignTask,
  updateTask,
  type Task,
  type TaskCandidate,
  type TaskInput,
} from '../api/tasks.ts';
import { EditButton, RemoveButton } from '../components/action-buttons.tsx';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { TextArea } from '../components/text-area.tsx';

// CB-REQ OPS-01, OPS-02, OPS-10 and CRW-11 at v1.996 – v1.997, crew part 4A: the work a job needs.
// A labour or transport line makes its task, kept in step by the database; others are added here.
// Each is carried by shifts — Assign… lists the shifts near it with how each fits, and a mismatch
// asks before it is saved. How full a task is, is read: n of m. The job's lead is someone on one of
// its tasks. From crew part 4B the rows and forms live on the roster, for any task on a job or none,
// and the job page shows its crew in summary (OPS-03 at v1.998).

const REASON = {
  line_removed: 'line removed',
  job_cancelled: 'job cancelled',
  office: 'set aside',
};

function useRefresh() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    await queryClient.invalidateQueries({ queryKey: ['shifts'] });
    await queryClient.invalidateQueries({ queryKey: ['roster'] });
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };
}

export function TaskRow({
  task,
  job,
  editable,
}: {
  task: Task;
  /** On the job's page, its phases are one tap away when editing. */
  job?: Job | undefined;
  editable: boolean;
}) {
  const refresh = useRefresh();
  const [open, setOpen] = useState<'edit' | 'assign' | null>(null);
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: refresh,
  });
  const t = task;
  const retired = t.status === 'not_needed';

  if (open === 'edit') {
    return (
      <li>
        <TaskForm job={job} task={t} onDone={() => setOpen(null)} />
      </li>
    );
  }

  return (
    <li className={`panel flex flex-col gap-2 p-4 ${retired ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="font-semibold">
            {t.description}
            {t.role ? <span className="text-ink-soft"> · {t.role.name}</span> : null}
          </p>
          <p className="text-sm text-ink-soft">
            {localLabel(t.window.localStart)} – {t.window.localEnd.slice(11)}
            {t.venue ? ` · ${t.venue.name}` : ''}
            {t.room ? ` · ${t.room.name}` : ''}
          </p>
          {t.crewNotes ? <p className="text-sm">For crew: {t.crewNotes}</p> : null}
          {t.officeNotes ? <p className="text-sm text-ink-soft">Office: {t.officeNotes}</p> : null}
          {t.assignments.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5 text-sm">
              {t.assignments.map((a) => (
                <li key={a.shiftId} className="flex items-center gap-1">
                  <span
                    className={`tag ${a.fit === 'fits' && !a.venueDiffers ? '' : 'tag-accent-2'}`}
                  >
                    {a.crewMember?.name ?? 'Open shift'}
                    {a.fit !== 'fits' ? ` · ${FIT[a.fit]}` : ''}
                    {a.venueDiffers ? ' · other venue' : ''}
                  </span>
                  {editable && !retired ? (
                    <RemoveButton
                      what={`${a.crewMember?.name ?? 'open shift'} from ${t.description}`}
                      disabled={act.isPending}
                      onClick={() => act.mutate(() => unassignTask(t.id, a.shiftId))}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-1">
          {retired ? (
            <span className="tag">
              Not needed{t.retiredReason ? ` · ${REASON[t.retiredReason]}` : ''}
            </span>
          ) : (
            <>
              <span className={`tag ${t.fill === 'full' ? 'tag-accent' : ''}`}>
                {t.status === 'covered' ? 'Covered' : `${t.filled} of ${t.crewNeeded}`}
              </span>
              {t.status === 'done' ? <span className="tag">Done</span> : null}
              {t.fill === 'over' ? <span className="tag tag-accent-2">Over</span> : null}
              {/* OPS-01: a line's task whose times are no longer the job's sold window. */}
              {t.offSold ? <span className="tag tag-accent-2">Off sold times</span> : null}
            </>
          )}
        </div>
      </div>

      {editable ? (
        <div className="flex flex-wrap gap-2">
          {!retired ? (
            <>
              <Button onClick={() => setOpen(open === 'assign' ? null : 'assign')}>Assign…</Button>
              {t.status === 'open' ? (
                <Button
                  variant="quiet"
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => setTaskStatus(t.id, 'covered'))}
                >
                  Covered
                </Button>
              ) : null}
              {t.status !== 'done' ? (
                <Button
                  variant="quiet"
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => setTaskStatus(t.id, 'done'))}
                >
                  Done
                </Button>
              ) : null}
              {t.status !== 'open' ? (
                <Button
                  variant="quiet"
                  disabled={act.isPending}
                  onClick={() => act.mutate(() => setTaskStatus(t.id, 'open'))}
                >
                  Reopen
                </Button>
              ) : null}
              <Button
                variant="quiet"
                disabled={act.isPending}
                onClick={() => act.mutate(() => setTaskStatus(t.id, 'not_needed'))}
              >
                Not needed
              </Button>
              <EditButton what={t.description} onClick={() => setOpen('edit')} />
            </>
          ) : t.retiredReason === 'office' ? (
            <Button
              variant="quiet"
              disabled={act.isPending}
              onClick={() => act.mutate(() => setTaskStatus(t.id, 'open'))}
            >
              Reopen
            </Button>
          ) : null}
        </div>
      ) : null}

      {open === 'assign' ? <Candidates task={t} onClose={() => setOpen(null)} /> : null}
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </li>
  );
}

/**
 * OPS-02 and OPS-10 at v1.996: the shifts near a task, fitting ones first. One that doesn't fit, or
 * is at another venue, asks before it is saved — the database refuses it unconfirmed.
 */
function Candidates({ task, onClose }: { task: Task; onClose: () => void }) {
  const refresh = useRefresh();
  const candidates = useQuery(taskCandidatesQuery(task.id));
  const [asking, setAsking] = useState<{ shiftId: string; message: string } | null>(null);
  const assign = useMutation({
    mutationFn: ({ shiftId, confirm }: { shiftId: string; confirm: boolean }) =>
      assignTask(task.id, shiftId, confirm),
    onSuccess: async () => {
      setAsking(null);
      await refresh();
    },
    onError: (error, { shiftId }) => {
      if (error instanceof MismatchProblem) setAsking({ shiftId, message: error.message });
    },
  });
  const open = (candidates.data ?? []).filter((c) => !c.assigned);
  return (
    <div className="flex flex-col gap-2 rounded-md bg-tile p-3">
      <p className="text-sm font-semibold">Assign to…</p>
      {candidates.isPending ? <p className="text-muted">Loading…</p> : null}
      {candidates.isError ? (
        <p role="alert" className="alert">
          {candidates.error.message}
        </p>
      ) : null}
      {candidates.data && open.length === 0 ? (
        <p className="text-sm text-ink-soft">No shifts that day. Book someone under Shifts.</p>
      ) : null}
      <ul className="flex flex-col gap-1">
        {open.map((c) => (
          <li key={c.shiftId} className="flex flex-wrap items-center gap-2">
            <Button
              variant="quiet"
              disabled={assign.isPending}
              onClick={() => assign.mutate({ shiftId: c.shiftId, confirm: false })}
            >
              {c.crewMember?.name ?? 'Open shift'}
            </Button>
            <span className="text-sm text-ink-soft">{candidateWhen(c)}</span>
            <span className={`tag ${c.fit === 'fits' ? '' : 'tag-accent-2'}`}>{FIT[c.fit]}</span>
            {c.venueDiffers ? <span className="tag tag-accent-2">Other venue</span> : null}
            {c.complianceGaps.length > 0 ? (
              <span className="tag tag-accent-2">Needs {gapsLabel(c.complianceGaps)}</span>
            ) : null}
          </li>
        ))}
      </ul>
      {asking ? (
        <div role="alert" className="notice flex flex-wrap items-center gap-2 text-sm">
          <span>{asking.message}</span>
          <Button
            disabled={assign.isPending}
            onClick={() => assign.mutate({ shiftId: asking.shiftId, confirm: true })}
          >
            Assign anyway
          </Button>
          <Button variant="quiet" onClick={() => setAsking(null)}>
            Back
          </Button>
        </div>
      ) : null}
      {assign.isError && !(assign.error instanceof MismatchProblem) ? (
        <p role="alert" className="alert">
          {assign.error.message}
        </p>
      ) : null}
      <Button variant="quiet" className="self-start" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

const candidateWhen = (c: TaskCandidate) =>
  `${localLabel(c.window.localStart)} – ${c.window.localEnd.slice(11)}${
    c.venue ? ` · ${c.venue.name}` : c.location ? ` · ${c.location.name}` : ''
  }`;

/** CRW-11 at v1.997: the job's lead, chosen from the people on its tasks. */
export function Lead({ job, tasks, editable }: { job: Job; tasks: Task[]; editable: boolean }) {
  const refresh = useRefresh();
  const people = new Map<string, string>();
  for (const t of tasks) {
    for (const a of t.assignments) if (a.crewMember) people.set(a.crewMember.id, a.crewMember.name);
  }
  // One who has lost their last task is still named: from the crew list.
  const crew = useQuery({
    ...crewQuery(false),
    enabled: !!job.leadCrewMemberId && !people.has(job.leadCrewMemberId),
  });
  const leadName = job.leadCrewMemberId
    ? (people.get(job.leadCrewMemberId) ??
      crew.data?.find((c) => c.id === job.leadCrewMemberId)?.displayName ??
      'Someone')
    : null;
  const save = useMutation({
    mutationFn: (leadCrewMemberId: string | null) => updateJob(job.id, { leadCrewMemberId }),
    onSuccess: refresh,
  });
  if (!editable && !leadName) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label htmlFor={`lead-${job.id}`} className="text-ink-soft">
        Lead
      </label>
      {editable ? (
        <select
          id={`lead-${job.id}`}
          className="input"
          value={job.leadCrewMemberId ?? ''}
          disabled={save.isPending}
          onChange={(e) => save.mutate(e.currentTarget.value || null)}
        >
          <option value="">None</option>
          {job.leadCrewMemberId && !people.has(job.leadCrewMemberId) ? (
            <option value={job.leadCrewMemberId}>{leadName}</option>
          ) : null}
          {[...people].map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      ) : (
        <span id={`lead-${job.id}`}>{leadName}</span>
      )}
      {job.leadOffJob ? <span className="tag tag-accent-2">Lead off job</span> : null}
      {save.isError ? (
        <span role="alert" className="text-accent-2-700">
          {save.error.message}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A task by hand, or a change to one; times on the job's clock, its phases one tap away. Off a job's
 * page (the roster), a new task is put on a job, or none, and starts on the day given.
 */
export function TaskForm({
  job,
  task,
  date,
  drawn,
  onDone,
}: {
  job?: Job | undefined;
  task?: Task;
  /** Off a job's page: the local date a new task defaults to. */
  date?: string | undefined;
  /** OPS-03 at v1.9105: a window drawn on the roster's job band, and that job. */
  drawn?: { start: string; end: string; jobId?: string | undefined } | undefined;
  onDone: () => void;
}) {
  const refresh = useRefresh();
  const roles = useQuery(rolesQuery);
  const choosesJob = !job && !task;
  const jobs = useQuery({ ...jobsQuery, enabled: choosesJob });
  const [jobId, setJobId] = useState(drawn?.jobId ?? '');
  const day = date ?? '';
  const first = job
    ? job.eventWindows
      ? job.eventWindows.event
      : { localStart: job.soldWindow.localStart, localEnd: job.soldWindow.localEnd }
    : { localStart: `${day}T08:00`, localEnd: `${day}T16:00` };
  const [description, setDescription] = useState(task?.description ?? '');
  const [roleId, setRoleId] = useState(task?.role?.id ?? '');
  const [start, setStart] = useState(
    drawn?.start ?? (task?.window ?? first).localStart.slice(0, 16),
  );
  const [end, setEnd] = useState(drawn?.end ?? (task?.window ?? first).localEnd.slice(0, 16));
  const [crewNeeded, setCrewNeeded] = useState(String(task?.crewNeeded ?? 1));
  const [crewNotes, setCrewNotes] = useState(task?.crewNotes ?? '');
  const [officeNotes, setOfficeNotes] = useState(task?.officeNotes ?? '');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (input: TaskInput) =>
      task
        ? updateTask(task.id, input)
        : createTask({ ...input, jobId: job?.id ?? (jobId || null) }),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  const phases = !job
    ? []
    : job.eventWindows
      ? ([
          ['Bump-in', job.eventWindows.bumpIn],
          ['Event', job.eventWindows.event],
          ['Bump-out', job.eventWindows.bumpOut],
        ] as const)
      : ([['Sold times', job.soldWindow]] as const);
  // Live jobs to put it on: not cancelled, not over.
  const open = (jobs.data ?? []).filter((j) => j.status === 'confirmed' || j.status === 'out');

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!description.trim()) return setProblem('Say what the task is.');
    const needed = Number(crewNeeded);
    if (!Number.isInteger(needed) || needed < 1)
      return setProblem('People: a whole number, 1 or more.');
    if (!start || !end || end <= start) return setProblem('The task ends after it starts.');
    setProblem(undefined);
    save.mutate({
      description: description.trim(),
      roleId: roleId || null,
      window: { start, end },
      crewNeeded: needed,
      crewNotes: crewNotes.trim() || null,
      officeNotes: officeNotes.trim() || null,
    });
  }

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4" aria-label="Task">
      {choosesJob ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="task-job" className="text-xs text-ink-soft">
            Job
          </label>
          <select
            id="task-job"
            className="input"
            value={jobId}
            onChange={(e) => setJobId(e.currentTarget.value)}
          >
            <option value="">None</option>
            {open.map((j) => (
              <option key={j.id} value={j.id}>
                {j.reference} {j.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-[1fr_12rem_6rem]">
        <Field
          label="Task"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="task-role" className="text-xs text-ink-soft">
            Role
          </label>
          <select
            id="task-role"
            className="input"
            value={roleId}
            onChange={(e) => setRoleId(e.currentTarget.value)}
          >
            <option value="">None</option>
            {(roles.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="People"
          inputMode="numeric"
          value={crewNeeded}
          onChange={(e) => setCrewNeeded(e.currentTarget.value)}
        />
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="text-xs text-ink-soft">When</legend>
        {phases.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {phases.map(([label, w]) => (
              <Button
                key={label}
                type="button"
                variant="quiet"
                onClick={() => {
                  setStart(w.localStart.slice(0, 16));
                  setEnd(w.localEnd.slice(0, 16));
                }}
              >
                {label}
              </Button>
            ))}
          </div>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Starts"
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.currentTarget.value)}
          />
          <Field
            label="Ends"
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.currentTarget.value)}
          />
        </div>
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextArea
          label="Notes for the crew"
          rows={2}
          value={crewNotes}
          onChange={(e) => setCrewNotes(e.currentTarget.value)}
        />
        <TextArea
          label="Office notes"
          hint="Never shown to crew."
          rows={2}
          value={officeNotes}
          onChange={(e) => setOfficeNotes(e.currentTarget.value)}
        />
      </div>
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {task ? 'Save' : 'Add'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
